import React, { ReactNode } from 'react';
import { Box, Container } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import Busy from '../components/Busy';
import ErrorBoundary from '../components/ErrorBoundary';
import { APIUnauthenticatedService, APIUnauthenticatedServiceContext } from '../helpers/APICallsUnauthenticated';
import { APIAuthenticatedServiceContext } from '../helpers/APICallsAuthenticated';
import { useConfigInfoService } from '../helpers/ConfigInfo';
import { useAppApiService, useAppRuntimeState } from '../appCore/AppRuntimeReact';
import { getReadyScreen, isBusyNode } from '../appCore/AppRuntime';
import TandCForm from '../forms/TandCForm';

interface ReferralsAppParentProps {
  showIfLoggedOut: ReactNode;
  showIfLoggedIn: ReactNode;
}

const ReferralsAppParentContent: React.FC<ReferralsAppParentProps> = ({
  showIfLoggedIn,
  showIfLoggedOut,
}) => {
  const state = useAppRuntimeState();
  const apiService = useAppApiService();
  const configInfoService = useConfigInfoService();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admins');
  const showLoggedOut = state.node === 'unauthenticated';
  const showBusy = isBusyNode(state.node);
  const showAuthenticatedShell =
    state.node === 'awaitingTermsConsent' ||
    state.node === 'userMissing' ||
    getReadyScreen(state.node) !== null;
  const showError = state.node === 'error';

  if (showError) {
    return (
      <Box sx={{ textAlign: 'left' }}>
        <h1>Error.... Please Refresh the web page</h1>
        <h3>{state.lastError ? `Error Message: ${state.lastError}` : ''}</h3>
      </Box>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={state.node}
        style={{ width: '100%' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ErrorBoundary>
          <Container
            style={{
              // REQ-026: admin tables grow from the left edge instead of being centered in the normal 800px app column.
              maxWidth: isAdminRoute ? 'none' : '800px',
              padding: '0px',
            }}
          >
            {showLoggedOut && (
              <APIUnauthenticatedServiceContext.Provider
                value={APIUnauthenticatedService.getInstance(configInfoService)}
              >
                {showIfLoggedOut}
              </APIUnauthenticatedServiceContext.Provider>
            )}

            {showBusy && <Busy message="Loading..." />}

            {showAuthenticatedShell && apiService && (
              <APIAuthenticatedServiceContext.Provider value={apiService}>
                {state.node === 'awaitingTermsConsent' && <TandCForm />}
                {state.node === 'userMissing' && <p>create user form</p>}
                {getReadyScreen(state.node) !== null && showIfLoggedIn}
              </APIAuthenticatedServiceContext.Provider>
            )}

            {showAuthenticatedShell && !apiService && <Busy message="Loading..." />}
          </Container>
        </ErrorBoundary>
      </motion.div>
    </AnimatePresence>
  );
};

const ReferralsAppParent: React.FC<ReferralsAppParentProps> = ({
  showIfLoggedIn,
  showIfLoggedOut,
}) => (
  <ReferralsAppParentContent
    showIfLoggedIn={showIfLoggedIn}
    showIfLoggedOut={showIfLoggedOut}
  />
);

export default ReferralsAppParent;
